/* eslint-disable */
import React, { useEffect, useRef, useState } from 'react';
import { Button, Grid } from '@mui/material';
import { CloseIcon } from 'src/icons';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import moment from 'moment';
import useYupValidationResolver from 'src/hooks/useYupValidationResolver';
import * as yup from 'yup';
import { useRoleUser } from 'src/hooks/query/usersByRole';
import { scrollToError } from 'src/utils/FormUtils';
import RHFAutoComplete from '../elements/AutoComplete/RHFAutoComplete';
import RHFEditor from '../elements/Editor/RHFEditor';
import RHFEmailsChips from '../elements/EmailsChips/RHFEmailsChips';
import RHFInput from '../elements/Input/RHFInput';
import RHFDatePicker from '../elements/CustomDatePicker/RHFDatePicker';
import RHFTimePicker from '../elements/CustomTimePicker/RHFTimePicker';

const titles = [
  'ONBOARDING GATE PASS',
  'FACE 2 FACE',
];

export interface emailValues {
  to: Array<string>;
  cc?: Array<string> | null;
  subject: string;
  content: string;
  otherInterviewers?: Array<string>;
}

export interface RequestGatePassFormValues extends emailValues {
  date: string;
  time: string;
  interviewer: number;
  otherInterviewers?: Array<string>;
}

const validationSchema = (title: string | undefined) => yup.object().shape({
  to: yup.array(yup.string().email()).required('Must have at least 1 email'),
  cc: yup.array(yup.string().email()).optional(),
  subject: yup.string().required('subject required').min(1, 'Subject required'),
  content: yup.string().required('email content required'),
  date: yup.string().required('date is required'),
  time: yup.string().required('time is required'),
  // Conditionally apply interviewer validation based on title
  interviewer: title === 'ONBOARDING GATE PASS' ? yup.number().optional() : yup.number().required('Interviewer is required'),
});

interface RequestGatePassProps {
  onSubmit: (data: emailValues) => void;
  onCancel: () => void;
  loading: boolean;
  defaultValues?: emailValues | undefined;
  savedDraft?: Partial<RequestGatePassFormValues>;
  onDraftChange?: (data: Partial<RequestGatePassFormValues>) => void;
  title?: string;
}

const RequestGatePass = function RequestGatePass(props: RequestGatePassProps) {
  const {
    onSubmit,
    defaultValues,
    savedDraft,
    onDraftChange,
    onCancel,
    title,
  } = props;

  const resolver = useYupValidationResolver(validationSchema(title));
  const [disableSubmit, setDisableSubmit] = useState<boolean>(false);

  const { userByRoleOptionsWithEmail, usersLoading } = useRoleUser({
    roleNameList: 'ATSInterviewer',
  });

  const methods = useForm<RequestGatePassFormValues>({
    defaultValues,
    resolver,
  });

  const {
    handleSubmit,
    getValues,
    watch,
  } = methods;

  const hasInitialized = useRef(false);

  const onError = () => {
    scrollToError('request-gatepass-form');
  };

  useEffect(() => {
    if (!defaultValues || hasInitialized.current) return;
    hasInitialized.current = true;
    methods.reset({
      ...defaultValues,
      ...savedDraft,
      to: savedDraft?.to || defaultValues?.to || [],
      cc: savedDraft?.cc ?? defaultValues?.cc,
      subject: savedDraft?.subject || defaultValues?.subject || '',
      content: savedDraft?.content || defaultValues?.content || '',
    });
  }, [defaultValues, savedDraft, methods]);

  useEffect(() => {
    if (!onDraftChange) return undefined;
    const subscription = watch((formValues) => {
      onDraftChange(formValues as Partial<RequestGatePassFormValues>);
    });
    return () => subscription.unsubscribe();
  }, [watch, onDraftChange]);

  const submitHandler: SubmitHandler<RequestGatePassFormValues> = async (data) => {
    setDisableSubmit(true);
    const {
      date = '',
      time = '',
    } = data;

    const interviewDate = date && moment(date).format('MM/DD/YYYY');
    const interviewTime = time && moment(time).format('hh:mm A');
    const request = {
      ...data,
      interviewerId: data?.interviewer,
      date: (interviewDate || ''),
      time: (interviewTime || ''),
    };

    await onSubmit(request);
    setDisableSubmit(false);
  };

  const replaceTemplateValues = (template: string, key: string, value: string) => {
    return template.replaceAll(key, value);
  };

  const getValueFromFormAndUpdateTemplate = (dynamicKey: string, value: string) => {
    const template = defaultValues?.content || '';
    let modified = template;

    const {
      date,
      time,
      interviewer,
    } = getValues();

    const dynamicKeys = {
      date: title !== 'ONBOARDING GATE PASS' ? '[$INTERVIEW_DATE$]' : '[$START_DATE$]',
      time: title !== 'ONBOARDING GATE PASS' ? '[$INTERVIEW_TIME$]' : '[$START_TIME$]',
      interviewer: '[$INTERVIEWER_NAME$]',
    };

    const getFieldValue = (key: keyof typeof dynamicKeys): string => {
      const placeholder = dynamicKeys[key];
      if (placeholder === dynamicKey) {
        return value;
      }

      const raw = { date, time, interviewer }[key];
      if (!raw) return '';

      if (key === 'date') {
        return moment(raw).format('MMMM Do, YYYY');
      }
      if (key === 'time') {
        return moment(raw).format('hh:mm A');
      }
      return userByRoleOptionsWithEmail.find(({ value: optionValue }) => optionValue === raw)?.label || '';
    };

    Object.keys(dynamicKeys).forEach((key) => {
      const typedKey = key as keyof typeof dynamicKeys;
      const fieldValue = getFieldValue(typedKey);
      if (fieldValue) {
        modified = replaceTemplateValues(modified, dynamicKeys[typedKey], fieldValue);
      }
    });

    methods.setValue('content', modified);
  }

  return (
    <div id="request-gatepass-form">
      <FormProvider {...methods}>
        <form className="bg-white  p-10 flex gap-8 justify-center items-center flex-col">
          {title && (
            <div className="flex justify-center items-center w-full text-[#28364C] font-medium text-base">
              {title}
            </div>
          )}

          <div className="flex w-full p-4 flex-col gap-10">
            <div className="w-full flex-shrink-0">

              <Grid container spacing={3} mb={4}>
                <Grid item xs={12} sm={12} md={6} lg={4}>
                  <RHFDatePicker
                    control={methods.control}
                    name='date'
                    label='Date'
                    minDate={moment(new Date())}
                    onDateChange={(event: any) => {
                      getValueFromFormAndUpdateTemplate(
                        title !== 'ONBOARDING GATE PASS' ? '[$INTERVIEW_DATE$]' : '[$START_DATE$]',
                        event.format('MMMM Do, YYYY'),
                      )  
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={12} md={6} lg={4}>
                  <RHFTimePicker
                    control={methods.control}
                    name='time'
                    label='Time'
                    onTimeChange={(event: any) => {
                      getValueFromFormAndUpdateTemplate(
                        title !== 'ONBOARDING GATE PASS' ? '[$INTERVIEW_TIME$]' : '[$START_TIME$]',
                        event.format('hh:mm A'),
                      );
                    }}
                  />
                </Grid>
              </Grid >
            </div >

            {
              titles.includes(title as string) && (
              <>
                <div className='w-[300px]'>
                    <RHFAutoComplete
                      name="interviewer"
                      label="Interviewer"
                      control={methods.control}
                      options={userByRoleOptionsWithEmail}
                      loading={usersLoading}
                      placeholder="Select interviewer"
                      onSelectChange={(event: any) => {
                        getValueFromFormAndUpdateTemplate(
                          '[$INTERVIEWER_NAME$]',
                          userByRoleOptionsWithEmail.find(({ value }) => value === event as number)?.label || '',
                        );
                      }}
                    />
                  </div>
                <div className="w-full flex-shrink-0 pt-3">
                  <RHFEmailsChips
                    control={methods.control}
                    name="otherInterviewers"
                    label="Additional Interviewers"
                    placeholder="Enter emails"
                  />
                </div>
              </>
              )
            }
            <div className="w-full flex-shrink-0">
              <RHFEmailsChips
                control={methods.control}
                name="to"
                label="To"
                placeholder="Enter emails"
              />
            </div>
            <div className="w-full flex-shrink-0">
              <RHFEmailsChips
                control={methods.control}
                name="cc"
                label="cc"
                placeholder="Enter emails"
              />
            </div>
            <div className="w-full flex-shrink-0">
              <RHFInput
                control={methods.control}
                name="subject"
                label="Subject"
                placeholder="Subject"
              />
            </div>
            <div className="w-full min-h-[200px] flex-shrink-0">
              <RHFEditor
                control={methods.control}
                name="content"
              />
            </div>
          </div >
          <div className="w-full flex  gap-2 ">
            <Button variant="contained" type="submit" size="small" className="font-medium rounded-full" disabled={disableSubmit} onClick={handleSubmit(submitHandler, onError)}>Send</Button>
            <Button variant="outlined" size="small" className="font-medium rounded-full" onClick={onCancel} disabled={disableSubmit}>Cancel</Button>
          </div>
          <div
            className="absolute right-2 top-2"
            onClick={onCancel}
          >
            <CloseIcon className="cursor-pointer" />
          </div>
        </form >
      </FormProvider >
    </div>
  );
};

RequestGatePass.defaultProps = {
  title: '',
};

RequestGatePass.propTypes = {

};

export default RequestGatePass;
